<template>
  <div>
    <div
      class="h-13 flex"
      @mouseover="hoverState = true"
      @mouseleave="hoverState = false"
    >
      <div class="sticky left-60 z-10 pl-6 flex bg-slate-100">
        <div
          class="w-20 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300 border-l"
          :class="[
            isLast ? 'rounded-bl' : '',
            hoverState ? 'bg-hover-state' : 'bg-pure-white'
          ]"
        >
          <BlTextBody2 :text="user.id" class="truncate text-slate-1000" />
        </div>
        <div
          class="w-60 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300 border-r gap-2"
          :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
        >
          <BlTextBody2
            :title="user.name"
            :text="user.name"
            class="truncate text-slate-1000"
          />
          <BlChip v-if="user.is_new" chip="New" color="grass" dense />
          <BlChip v-if="user.is_owner" chip="Owner" color="tulip" dense />
        </div>
      </div>
      <div
        class="w-50 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :title="user.email" :text="user.email" class="truncate" />
      </div>
      <div
        class="w-50 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="userRoles[user.role]" class="truncate" />
      </div>
      <div
        class="w-50 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlChip
          :chip="userStatuses[user.status].chip"
          :color="userStatuses[user.status].color"
          dense
        />
      </div>
      <div
        class="w-50 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :text="
            displayDate({
              date: user.created_at,
              format: 'DD/MM/YYYY HH:mm'
            })
          "
          class="truncate"
        />
      </div>
      <div
        class="w-50 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :text="
            displayDate({
              date: user.deactivated_at,
              format: 'DD/MM/YYYY HH:mm'
            })
          "
          class="truncate"
        />
      </div>
      <div
        class="w-50 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :text="userRestrictionTypes[user.restriction_type]"
          class="truncate"
        />
      </div>
      <div
        class="w-50 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 v-if="!userAccessTags.length" text="-" />
        <div v-else class="flex items-center gap-2">
          <div
            v-for="(tag, idx) in userAccessTags.slice(0, 2)"
            :key="idx"
            :title="tag.name || '-'"
            class="h-7 min-w-8 max-w-20 px-2 flex items-center justify-center rounded truncate"
            :class="{ 'bg-slate-300': !tag.background_color }"
            :style="{ backgroundColor: tag.background_color || 'unset' }"
          >
            <BlTextBody2
              :text="tag.name || '-'"
              class="truncate"
              :class="{ 'text-slate-800': !tag.color }"
              :style="{ color: tag.color || 'unset' }"
            />
          </div>
          <div class="relative" v-if="userAccessTags.slice(2).length">
            <div
              class="flex items-center justify-center cursor-pointer"
              @click="moreTagsOpened = !moreTagsOpened"
            >
              <BlBubbleCount
                :count="`+ ${userAccessTags.slice(2).length}`"
                class="h-7 hover:bg-light-purple-100"
              />
            </div>
            <div
              v-if="moreTagsOpened"
              class="absolute z-50 top-0 right-0 mt-10 py-1 px-1 flex flex-col gap-1 bg-pure-white rounded border border-slate-400"
            >
              <div
                v-for="(tag, idx) in userAccessTags.slice(2)"
                :key="idx"
                :title="tag.name || '-'"
                class="min-w-40 max-w-64 py-2 px-4 flex text-left rounded truncate"
              >
                <BlTextBody2 class="truncate" :text="tag.name || '-'" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="sticky right-0 z-10 pr-6 flex bg-slate-100 flex-grow">
        <div
          class="w-12 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300 border-r justify-center flex-grow"
          :class="[
            isLast ? 'rounded-br' : '',
            hoverState ? 'bg-hover-state' : 'bg-pure-white'
          ]"
        >
          <div class="relative">
            <BlInputButton
              type="plain"
              dense
              icon="dots-vertical"
              class="hover:bg-hover-state"
              @clicked="menuOpened = !menuOpened"
            />
            <div
              v-if="menuOpened"
              class="absolute z-50 top-0 right-full mr-3 py-2 px-2 flex flex-col gap-1 bg-pure-white rounded border border-slate-400"
            >
              <!-- <div
                v-if="user.status !== 'pending'"
                class="px-4 py-2 min-w-64 rounded hover:bg-hover-state cursor-pointer"
                @click="onOpenSessionAnalytics"
              >
                <BlTextBody2 class="text-slate-1000" text="View analytics" />
              </div> -->
              <div
                class="px-4 py-2 min-w-64 rounded hover:bg-hover-state cursor-pointer"
                @click="editRoleOpened = true"
              >
                <BlTextBody2 class="text-slate-1000" text="Edit role" />
              </div>
              <div
                class="px-4 py-2 min-w-64 rounded hover:bg-hover-state cursor-pointer"
                @click="editAccessOpened = true"
              >
                <BlTextBody2 class="text-slate-1000" text="Edit access" />
              </div>
              <div
                v-if="user.status !== 'deactivated'"
                class="px-4 py-2 min-w-64 rounded hover:bg-hover-state cursor-pointer"
                @click="resetPasswordOpened = true"
              >
                <BlTextBody2 class="text-slate-1000" text="Reset password" />
              </div>
              <div id="line-sparators" class="h-px w-full bg-slate-400"></div>
              <div
                v-if="user.status === 'active'"
                class="px-4 py-2 min-w-64 rounded hover:bg-hover-state cursor-pointer"
                @click="deactivateOpened = true"
              >
                <BlTextBody2 class="text-slate-1000" text="Deactivate member" />
              </div>
              <div
                v-if="user.status === 'deactivated'"
                class="px-4 py-2 min-w-64 rounded hover:bg-hover-state cursor-pointer"
                @click="onReactivate"
              >
                <BlTextBody2 class="text-slate-1000" text="Reactivate member" />
              </div>
              <div
                class="px-4 py-2 min-w-64 rounded hover:bg-hover-state cursor-pointer"
                @click="deleteOpened = true"
              >
                <BlTextBody2 class="text-error-400" text="Delete member" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <EditUserRoleModal
      v-if="editRoleOpened"
      :user="user"
      @close-modal="editRoleOpened = false"
    />

    <EditUserAccessModal
      v-if="editAccessOpened"
      :user="user"
      :centerId="centerId"
      @close-modal="editAccessOpened = false"
    />

    <ResetUserPasswordModal
      v-if="resetPasswordOpened"
      :user="user"
      @close-modal="resetPasswordOpened = false"
    />

    <DeactivatedUserModal
      v-if="deactivateOpened"
      :user="user"
      @close-modal="deactivateOpened = false"
    />

    <DeletedUserModal
      v-if="deleteOpened"
      :user="user"
      @close-modal="deleteOpened = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate } from "@/plugins/helper.js";

import EditUserRoleModal from "../../../team/EditUserRoleModal.vue";
import EditUserAccessModal from "../../../team/EditUserAccessModal.vue";
import ResetUserPasswordModal from "../../../team/ResetUserPasswordModal.vue";
import DeactivatedUserModal from "../../../team/DeactivatedUserModal.vue";
import DeletedUserModal from "../../../team/DeleteUserModal.vue";

import BlTextBody2 from "../../../BlTextBody2.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlChip from "../../../BlChip.vue";
import BlBubbleCount from "../../../BlBubbleCount.vue";

export default {
  name: "CenterTeamItem",
  components: {
    EditUserRoleModal,
    EditUserAccessModal,
    ResetUserPasswordModal,
    DeactivatedUserModal,
    DeletedUserModal,
    //
    BlTextBody2,
    BlInputButton,
    BlChip,
    BlBubbleCount
  },
  props: {
    user: { type: Object, required: true },
    centerId: { type: Number },
    isLast: { type: Boolean, default: false }
  },
  data() {
    return {
      hoverState: false,
      //
      userRoles: {
        admin: "Admin",
        staff: "Therapist",
        super_admin: "Super admin"
      },
      userStatuses: {
        active: { chip: "Active", color: "lime" },
        pending: { chip: "Account created", color: "purple" },
        deactivated: { chip: "Deactivated", color: "slate" }
      },
      userRestrictionTypes: {
        all_clients: "All clients",
        assigned_clients: "Assigned clients"
      },
      //
      moreTagsOpened: false,
      menuOpened: false,
      submitLoading: false,
      editRoleOpened: false,
      editAccessOpened: false,
      resetPasswordOpened: false,
      deactivateOpened: false,
      deleteOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    userAccessTags() {
      return [...this.user.branch_accesses, ...this.user.user_tags];
    }
  },
  watch: {
    moreTagsOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerMoreTags);
        }, 10);
      }
    },
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerMenu);
        }, 10);
      }
    }
  },
  async mounted() {},
  methods: {
    ...mapActions(["reactivateUser"]),
    displayDate,
    closeHandlerMoreTags() {
      window.removeEventListener("click", this.closeHandlerMoreTags);
      this.moreTagsOpened = false;
    },
    closeHandlerMenu() {
      window.removeEventListener("click", this.closeHandlerMenu);
      this.menuOpened = false;
    },
    //
    onOpenSessionAnalytics() {
      this.$router.push({
        name: "TeamSessionHistory",
        params: {
          user_id: this.user.id,
          redirect_target: `/super-admin/center/${this.centerId}/team`,
          redirect_label: "Center team (super admin panel)"
        }
      });
    },
    async onReactivate() {
      const params = {
        id: this.user.id,
        user: {
          ...this.user,
          status: "active",
          deactivated_at: null
        }
      };

      this.submitLoading = true;
      await this.reactivateUser(params);
      this.submitLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/center/centerTeam/Item.vue